import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { 
  Video, 
  Mic, 
  Wand2, 
  Download, 
  Sparkles, 
  Check,
  ArrowRight,
  Play,
  LogOut,
  Settings
} from "lucide-react";
import Footer from "../components/Footer";
import { useAuth } from "../contexts/AuthContext";

export default function LandingPage() {
  const navigate = useNavigate();
  const { currentUser, logout, loading } = useAuth();

  // If user is logged in and lands on home page, redirect to studio
  useEffect(() => {
    if (currentUser && !loading) {
      const urlParams = new URLSearchParams(window.location.search);
      // Only redirect if not explicitly coming from logout or signup completion
      if (!urlParams.has('stay')) {
        console.log('🔄 User is logged in, redirecting to studio...');
        navigate('/studio');
      }
    }
  }, [currentUser, loading, navigate]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/?stay=true');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-gray-200 bg-white px-6 py-4 shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="logo-placeholder"></div>
            <h1 className="text-xl font-bold font-manrope text-gray-900">Podmixr Labs</h1>
          </div>
          <div className="flex items-center gap-3">
            {currentUser ? (
              <>
                <Button
                  variant="ghost"
                  onClick={() => navigate('/library')}
                  className="text-gray-600 hover:text-gray-900"
                >
                  Library
                </Button>
                <Button
                  onClick={() => navigate('/studio')}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Studio
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => navigate('/account')}
                  className="text-gray-600 hover:text-gray-900"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Account
                </Button>
                <Button
                  variant="ghost"
                  onClick={handleLogout}
                  className="text-gray-600 hover:text-gray-900"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Log Out
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="ghost"
                  onClick={() => navigate('/login')}
                  className="text-gray-600 hover:text-gray-900"
                >
                  Log In
                </Button>
                <Button
                  onClick={() => navigate('/signup')}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Get Started Free
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="px-6 py-20 bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 font-manrope">
            Professional Podcast Studio
            <br />
            <span className="text-blue-600">In Your Browser</span>
          </h2>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            Record, edit, and enhance your podcasts with AI-powered tools. No downloads, no complexity - just professional results.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => navigate('/signup')}
              size="lg"
              className="bg-blue-600 hover:bg-blue-700 text-white text-lg px-8 py-6"
            >
              Start Recording Free
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button
              onClick={() => navigate('/demo')}
              size="lg"
              variant="outline"
              className="text-lg px-8 py-6 border-2 border-gray-300 hover:bg-gray-50"
            >
              <Play className="w-5 h-5 mr-2" />
              Watch Demo
            </Button>
          </div>
          <p className="text-sm text-gray-500 mt-4">
            ✨ No credit card required • 🎙️ Free forever plan available
          </p>
        </div>
      </section>

      {/* Features Section */}
      <section className="px-6 py-20 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h3 className="text-4xl font-bold text-gray-900 mb-4 font-manrope">
              Everything You Need to Create Amazing Podcasts
            </h3>
            <p className="text-xl text-gray-600">
              Professional tools designed for creators of all levels
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <Card className="p-6 bg-white border-gray-200 shadow-md hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <Video className="w-6 h-6 text-blue-600" />
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-2">Record Anywhere</h4>
              <p className="text-gray-600">
                Connect your camera and microphone directly in your browser. No software to install, no complicated setup.
              </p>
            </Card>

            {/* Feature 2 */}
            <Card className="p-6 bg-white border-gray-200 shadow-md hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <Mic className="w-6 h-6 text-green-600" />
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-2">Teleprompter</h4>
              <p className="text-gray-600">
                Follow your script with our built-in teleprompter. Words fade as you speak, keeping you on track.
              </p>
            </Card>

            {/* Feature 3 */}
            <Card className="p-6 bg-white border-gray-200 shadow-md hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                <Wand2 className="w-6 h-6 text-purple-600" />
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-2">AI Enhancement</h4>
              <p className="text-gray-600">
                One-click audio enhancement. Remove noise, balance levels, and eliminate echo automatically.
              </p>
            </Card>

            {/* Feature 4 */}
            <Card className="p-6 bg-white border-gray-200 shadow-md hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                <Sparkles className="w-6 h-6 text-red-600" />
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-2">AI Transcript Editing</h4>
              <p className="text-gray-600">
                Edit transcripts with natural language. "Remove all 'um' and 'uh'" - AI does it instantly.
              </p>
            </Card>

            {/* Feature 5 */}
            <Card className="p-6 bg-white border-gray-200 shadow-md hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mb-4">
                <Download className="w-6 h-6 text-yellow-600" />
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-2">Multi-Platform Export</h4>
              <p className="text-gray-600">
                Download video for YouTube, audio for Spotify, and transcripts for show notes - all in one click.
              </p>
            </Card>

            {/* Feature 6 */}
            <Card className="p-6 bg-white border-gray-200 shadow-md hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mb-4">
                <Video className="w-6 h-6 text-indigo-600" />
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-2">Intro/Outro</h4>
              <p className="text-gray-600">
                Add professional intros, outros, and background music. Merge everything automatically.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="px-6 py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h3 className="text-4xl font-bold text-gray-900 mb-4 font-manrope">
              Simple, Transparent Pricing
            </h3>
            <p className="text-xl text-gray-600">
              Start free, upgrade when you need more
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Free Plan */}
            <Card className="p-8 bg-white border-2 border-gray-200 shadow-md">
              <h4 className="text-2xl font-bold text-gray-900 mb-2">Free</h4>
              <p className="text-gray-600 mb-6">Perfect for getting started</p>
              <div className="mb-6">
                <span className="text-4xl font-bold text-gray-900">£0</span>
                <span className="text-gray-600">/month</span>
              </div>
              <Button 
                onClick={() => navigate('/signup')}
                className="w-full bg-gray-200 hover:bg-gray-300 text-gray-900 mb-6"
              >
                Start Free
              </Button>
              <ul className="space-y-3">
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">5 recordings per month</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Basic audio enhancement</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Teleprompter</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Download video & audio</span>
                </li>
              </ul>
            </Card>

            {/* Pro Plan */}
            <Card className="p-8 bg-white border-2 border-blue-600 shadow-lg relative">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-blue-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
                  Most Popular
                </span>
              </div>
              <h4 className="text-2xl font-bold text-gray-900 mb-2">Pro</h4>
              <p className="text-gray-600 mb-6">For serious podcasters</p>
              <div className="mb-6">
                <span className="text-4xl font-bold text-gray-900">£9.99</span>
                <span className="text-gray-600">/month</span>
              </div>
              <Button 
                onClick={() => navigate('/signup')}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white mb-6"
              >
                Start Pro Trial
              </Button>
              <ul className="space-y-3">
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700"><strong>Unlimited</strong> recordings</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">AI video enhancement</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">AI transcript editing</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Intro/outro merging</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Priority support</span>
                </li>
              </ul>
            </Card>

            {/* Enterprise Plan */}
            <Card className="p-8 bg-white border-2 border-gray-200 shadow-md">
              <h4 className="text-2xl font-bold text-gray-900 mb-2">Enterprise</h4>
              <p className="text-gray-600 mb-6">For teams and studios</p>
              <div className="mb-6">
                <span className="text-4xl font-bold text-gray-900">Custom</span>
              </div>
              <Button 
                onClick={() => navigate('/contact')}
                className="w-full bg-gray-900 hover:bg-gray-800 text-white mb-6"
              >
                Contact Sales
              </Button>
              <ul className="space-y-3">
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Everything in Pro</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Team collaboration</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Custom branding</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Dedicated support</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Custom integrations</span>
                </li>
              </ul>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-6 py-20 bg-blue-600">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="text-4xl font-bold text-white mb-6 font-manrope">
            Ready to Create Your First Podcast?
          </h3>
          <p className="text-xl text-blue-100 mb-8">
            Join thousands of creators using Podmixr Labs to produce professional podcasts
          </p>
          <Button
            onClick={() => navigate('/signup')}
            size="lg"
            className="bg-white hover:bg-gray-100 text-blue-600 text-lg px-8 py-6"
          >
            Get Started Free - No Credit Card Required
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
